import {
  Label as LabelHeadless,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react"
import { IconCertified } from "materia"
import { Fragment, type PropsWithChildren, type ReactNode } from "react"
import { modifiers } from "ui-tools"
import { Card } from "../Card"
import { Icon } from "../Icon"
import s from "./Dropdown.module.scss"

type DropdownProps<T, M> = M extends true
  ? {
      multiple: true
      value: T[]
      onChange: (value: T[]) => void
      children: ReactNode
    }
  : {
      multiple?: M
      value: T
      onChange: (value: T) => void
      children: ReactNode
    }

/*
 * Docs: https://headlessui.com/react/listbox
 */
export const Dropdown = <T, M extends boolean>({
  value,
  onChange,
  children,
  multiple,
}: DropdownProps<T, M>) => (
  <Listbox
    className={s.dropdown}
    // value/onChange correctness is guaranteed by DropdownProps; headlessui's
    // Listbox can't infer the single-vs-multiple shape from the runtime prop,
    // so bridge the handoff.
    value={value as T[]}
    onChange={onChange as (value: T[]) => void}
    multiple={multiple}
    as="div"
  >
    {children}
  </Listbox>
)

const Label = ({ children }: PropsWithChildren) => (
  <LabelHeadless className={s.label}>{children}</LabelHeadless>
)

const Button = ({ children }: PropsWithChildren) => (
  <ListboxButton as={Fragment}>
    {({ open }) => (
      <button className={modifiers(s, "button", { open })}>
        <div className={s.button__content}>{children}</div>
        <Icon icon="Chevron" className={s.button__chevron} />
      </button>
    )}
  </ListboxButton>
)

const CustomButton = ({ children }: PropsWithChildren) => (
  <ListboxButton as={Fragment}>
    {({ open }) => (
      <button className={modifiers(s, "customButton", { open })}>
        {children}
      </button>
    )}
  </ListboxButton>
)

const Options = ({ children }: PropsWithChildren) => (
  <Animation>
    <Card borderRadius="small">
      <ListboxOptions>{children}</ListboxOptions>
    </Card>
  </Animation>
)

type OptionProps<T> = {
  value: T
  children: ReactNode
}
const Option = <T,>({ value, children }: OptionProps<T>) => (
  <ListboxOption value={value} as={Fragment}>
    {({ focus, selected }) => (
      <li className={modifiers(s, "option", { focused: focus, selected })}>
        {children}
        {selected && <IconCertified />}
      </li>
    )}
  </ListboxOption>
)

const ChevronIcon = () => (
  <Icon icon="Chevron" className={s.customButton__chevron} />
)

Dropdown.Label = Label
Dropdown.Button = Button
Dropdown.CustomButton = CustomButton
Dropdown.Options = Options
Dropdown.Option = Option
Dropdown.ChevronIcon = ChevronIcon

/*
 * Docs: https://headlessui.com/react/transition
 */
const Animation = ({ children }: PropsWithChildren) => (
  <Transition
    enter={s["animation--enter"]}
    enterFrom={s["animation--enterFrom"]}
    enterTo={s["animation--enterTo"]}
    leave={s["animation--leave"]}
    leaveFrom={s["animation--leaveFrom"]}
    leaveTo={s["animation--leaveTo"]}
  >
    <div className={s.animation}>{children}</div>
  </Transition>
)
